<script>
import { defineComponent as _defineComponent } from "vue";
import { unref as _unref, renderSlot as _renderSlot, vShow as _vShow, mergeProps as _mergeProps, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["data-id", "data-idle"];
import { ref, inject, watch, nextTick, provide, computed } from "vue";
import { useMagicKeys } from "@vueuse/core";
import { useCommandItem } from "../composables/private/useCommandItem";
import { useCommandScroll } from "../composables/private/useCommandScroll";
import { useCommandCallback } from "../composables/private/useCommandCallback";
import {
  MagicCommandInstanceId,
  MagicCommandViewId,
  MagicCommandContentId,
  MagicCommandProviderOptions
} from "../symbols";
import { useCommandView } from "../composables/private/useCommandView";
import { useCommandState } from "../composables/private/useCommandState";
import "@maas/vue-equipment/utils/css/animations/fade-in.css";
import "@maas/vue-equipment/utils/css/animations/fade-out.css";
export default /* @__PURE__ */ _defineComponent({
  ...{
    inheritAttrs: false
  },
  __name: "MagicCommandContent",
  setup(__props) {
    const instanceId = inject(MagicCommandInstanceId, void 0);
    const viewId = inject(MagicCommandViewId, void 0);
    const elRef = ref(void 0);
    if (!instanceId) {
      throw new Error(
        "MagicCommandContent must be nested inside MagicCommandProvider"
      );
    }
    if (!viewId) {
      throw new Error("MagicCommandContent must be nested inside MagicCommandView");
    }
    const { getView } = useCommandView(instanceId);
    const view = getView(viewId);
    const { initializeState } = useCommandState(instanceId);
    const state = initializeState();
    const isActive = computed(() => view?.active && state.active && state.renderer);
    const isIdle = computed(() => state.input.view !== viewId);
    const options = inject(MagicCommandProviderOptions, void 0);
    const { activeItem, selectNextItem, selectPrevItem } = useCommandItem({
      instanceId,
      viewId
    });
    const {
      onBeforeEnter,
      onEnter,
      onAfterEnter,
      onBeforeLeave,
      onLeave,
      onAfterLeave
    } = useCommandCallback({
      instanceId,
      viewId
    });
    const {
      findElement,
      findScrollableAncestor,
      isElementAbove,
      isElementBelow,
      scrollInFromBottom,
      scrollInFromTop
    } = useCommandScroll(elRef);
    const keys = useMagicKeys();
    const nextTimeout = ref(void 0);
    const prevTimeout = ref(void 0);
    const nextInterval = ref(void 0);
    const prevInterval = ref(void 0);
    if (options?.keyListener?.next) {
      for (const key of options.keyListener.next) {
        watch(keys[key], (value) => {
          if (isIdle.value) {
            return;
          }
          if (value) {
            state.input.type = "keyboard";
            selectNextItem(options.loop);
            nextTimeout.value = setTimeout(() => {
              nextInterval.value = setInterval(() => {
                selectNextItem(options.loop);
              }, 100);
            }, 500);
          } else {
            clearTimeout(nextTimeout.value);
            clearInterval(nextInterval.value);
          }
        });
      }
    }
    if (options?.keyListener?.prev) {
      for (const key of options.keyListener.prev) {
        watch(keys[key], (value) => {
          if (isIdle.value) {
            return;
          }
          if (value) {
            state.input.type = "keyboard";
            selectPrevItem(options.loop);
            prevTimeout.value = setTimeout(() => {
              prevInterval.value = setInterval(() => {
                selectPrevItem(options.loop);
              }, 100);
            }, 500);
          } else {
            clearTimeout(prevTimeout.value);
            clearInterval(prevInterval.value);
          }
        });
      }
    }
    watch(
      activeItem,
      async (value) => {
        if (!value || isIdle.value || state.input.type !== "keyboard") {
          return;
        }
        nextTick(() => {
          const element = findElement(value.id);
          if (element) {
            const ancestor = findScrollableAncestor(element);
            switch (true) {
              case isElementAbove({ element, ancestor }):
                scrollInFromTop({ element, ancestor });
                break;
              case isElementBelow({ element, ancestor }):
                scrollInFromBottom({ element, ancestor });
                break;
            }
          }
        });
      },
      { deep: true }
    );
    provide(MagicCommandContentId, `${viewId}-content`);
    return (_ctx, _cache) => {
      return _unref(state).renderer && _unref(state).active ? (_openBlock(), _createBlock(_Teleport, {
        key: 0,
        to: _unref(state).renderer
      }, [
        _createVNode(_Transition, {
          name: _unref(state).options.transition?.content,
          "on-before-enter": _unref(onBeforeEnter),
          "on-enter": _unref(onEnter),
          "on-after-enter": _unref(onAfterEnter),
          "on-before-leave": _unref(onBeforeLeave),
          "on-leave": _unref(onLeave),
          "on-after-leave": _unref(onAfterLeave)
        }, {
          default: _withCtx(() => [
            isActive.value ? _withDirectives((_openBlock(), _createElementBlock("div", _mergeProps({
              ref_key: "elRef",
              ref: elRef,
              key: `${_unref(viewId)}-content`,
              class: "magic-command-content",
              "data-id": `${_unref(viewId)}-content`,
              "data-idle": isIdle.value
            }, _ctx.$attrs), [
              _renderSlot(_ctx.$slots, "default")
            ], 16, _hoisted_1)), [
              [_vShow, !isIdle.value]
            ]) : _createCommentVNode("v-if", true)
          ]),
          _: 3
          /* FORWARDED */
        }, 8, ["name", "on-before-enter", "on-enter", "on-after-enter", "on-before-leave", "on-leave", "on-after-leave"])
      ], 8, ["to"])) : _createCommentVNode("v-if", true);
    };
  }
});
</script>

<style>
.magic-command-content {
  position: absolute;
  inset: 0;
}

.magic-command-content[data-idle='true'] {
  pointer-events: none;
}

.magic-command-content-enter-active {
  animation: fade-in 150ms ease;
}

.magic-command-content-leave-active {
  animation: fade-out 150ms ease;
}
</style>
